import { Utils } from '../../../../../../common/js/Utils';
import { IllustrationHtml } from './IllustrationSummary';
import { CoverpathUtils } from '../../../../../js/static/CoverpathUtils';
import { DividendWithRtrColumnHeaderMap, ColumnHeaderDefWithRtrMap } from './RTR/NumericTableMaps';
import { RiderType } from '../../../../../../common/js/static/Product';
import { IllustrationRequest } from '../../../IllustrationRequest';
import { mmopmProductBlocks } from '../../../HtmlPdfTypes';

export class IllustrationBasedOnDiv {
  static dividendAdjustmentPercent = 1;
  static LowerDividendFooterMap;
  static SupplementalLowerDividendFooterMap;
  static termInforceColumnHeaders = [
    'policyYear',
    'endYearAge',
    'proratedGuaranteedAnnualBasePremiums',
    'proratedAnnualWaiverPremiums',
    'proratedAnnualTotalPremiums',
    'proratedCumulativeAnnualPremiums',
    'endYearDeathBenefitAmount',
  ];
  static termInforceColumnHeadersWithoutWaiver = [
    'policyYear',
    'endYearAge',
    'proratedGuaranteedAnnualBasePremiums',
    'proratedAnnualTotalPremiums',
    'proratedCumulativeAnnualPremiums',
    'endYearDeathBenefitAmount',
  ];
  static mmopm99TermInforceColumnHeaders = [
    'policyYear',
    'endYearAge',
    'proratedGuaranteedAnnualBasePremiums',
    'proratedAnnualWaiverPremiums',
    'proratedAnnualTotalPremiums',
    'proratedCumulativeAnnualPremiums',
    'endYearDeathBenefitAmount',
    'proratedNonGuaranteedAnnualTotalPremiums',
    'proratedNonGuaranteedCumulativeAnnualPremiums',
  ];
  static mmopm99TermInforceColumnHeadersWithoutWaiver = [
    'policyYear',
    'endYearAge',
    'proratedGuaranteedAnnualBasePremiums',
    'proratedAnnualTotalPremiums',
    'proratedCumulativeAnnualPremiums',
    'endYearDeathBenefitAmount',
    'proratedNonGuaranteedAnnualTotalPremiums',
    'proratedNonGuaranteedCumulativeAnnualPremiums',
  ];
  static dividendColumnHeaderMap = {
    PUA: [
      'policyYear',
      'ageEndYear',
      'contractPremiumBegYear',
      'guaranteedCashValueEndYear',
      'guaranteedDeathBenefitEndYear',
      'annualDividendEndYear',
      'cashValueOfAdditionsEndYear',
      'totalCashValueEndYear',
      'paidUpAdditionsEndYear',
      'totalDeathBenefitEndYear',
      'totalPaidUpInsuranceEndYear',
    ],
    Cash: [
      'policyYear',
      'ageEndYear',
      'contractPremiumBegYear',
      'guaranteedCashValueEndYear',
      'guaranteedDeathBenefitEndYear',
      'dividendPaidInCashBegYear',
      'taxOnDistributionBegYear',
      'annualNetOutlayBegYear',
      'totalCashValueEndYear',
      'totalDeathBenefitEndYear',
      'totalPaidUpInsuranceEndYear',
    ],
    PRCash: [
      'policyYear',
      'ageEndYear',
      'contractPremiumBegYear',
      'guaranteedCashValueEndYear',
      'guaranteedDeathBenefitEndYear',
      'netPremiumBegYear',
      'annualDividendEndYear',
      'dividendPaidInCashBegYear',
      'taxOnDistributionBegYear',
      'annualNetOutlayBegYear',
      'totalCashValueEndYear',
      'totalDeathBenefitEndYear',
      'totalPaidUpInsuranceEndYear',
    ],
    PRPUA: [
      'policyYear',
      'ageEndYear',
      'contractPremiumBegYear',
      'guaranteedCashValueEndYear',
      'guaranteedDeathBenefitEndYear',
      'netPremiumBegYear',
      'annualDividendEndYear',
      'cashValueOfAdditionsEndYear',
      'totalCashValueEndYear',
      'paidUpAdditionsEndYear',
      'totalDeathBenefitEndYear',
      'totalPaidUpInsuranceEndYear',
    ],
    Accumulate: [
      'policyYear',
      'ageEndYear',
      'contractPremiumBegYear',
      'guaranteedCashValueEndYear',
      'guaranteedDeathBenefitEndYear',
      'annualDividendEndYear',
      'taxOnDistributionBegYear',
      'annualNetOutlayBegYear',
      'cashValueOfAccumsEndYear',
      'totalCashValueEndYear',
      'totalDeathBenefitEndYear',
      'totalPaidUpInsuranceEndYear',
    ],
    Concept: [
      'policyYear',
      'ageEndYear',
      'annualOutlayBegYear',
      'annualLoan',
      'withdrawAmount',
      'annualNetOutlayBegYear',
      'annualDividendEndYear',
      'guaranteedCashValueEndYear',
      'cashValueOfAdditionsEndYear',
      'totalOutstandingLoanEndYear',
      'netCashValueEndYear',
      'paidUpAdditionsEndYear',
      'netDeathBenefitEndYear',
    ],
  };

  static getIllustrationTableBasedOnDiv(
    illustrationRequest,
    startIndex: number,
    noOfRows: number,
    basicIllustrationData,
    isWholeLifeInforce?: boolean,
    isWLFaceChange: boolean = false,
    tableColumns: string[] = [],
    columnHeaderText: string = ''
  ): string {
    const tabularValues = this.getTable(
      illustrationRequest,
      startIndex,
      noOfRows,
      basicIllustrationData,
      isWholeLifeInforce,
      isWLFaceChange,
      tableColumns,
      columnHeaderText
    );
    return tabularValues;
  }

  static getInforceIllustrationTableBasedOnDiv(
    startIndex: number,
    noOfRows: number,
    basicIllustrationData,
    hasWaiver: boolean,
    productBlock?: string
  ): string {
    const tabularValues = this.getInforceTable(startIndex, noOfRows, basicIllustrationData, hasWaiver, productBlock);
    return tabularValues;
  }

  static getInforceTable(
    startIndex: number,
    noOfRows: number,
    basicIllustrationData,
    hasWaiver: boolean,
    productBlock?: string
  ): string {
    //include non-guaranteed values for mmopm 99 series
    const is99series: boolean =
      productBlock === mmopmProductBlocks.MMOPMTerm[99] || productBlock === mmopmProductBlocks.MMOPMTermAPT80[99];
    let columnHeaders;
    if (is99series) {
      columnHeaders = hasWaiver ? TermInforceColumnHeadersMMOPM99 : TermInforceColumnHeadersMMOPM99WithoutWaiver;
    } else {
      columnHeaders = hasWaiver ? TermInforceColumnHeaders : TermInforceColumnHeadersWithoutWaiver;
    }
    const tableHtml =
      '<TABLE id="illustration-table">' +
      columnHeaders +
      this.getInforceRows(startIndex, noOfRows, basicIllustrationData, hasWaiver, is99series) +
      '</TABLE>';
    return tableHtml;
  }

  static getInforceRows(
    startIndex: number,
    noOfRows: number,
    basicIllustrationData,
    hasWaiver: boolean,
    is99series?: boolean
  ) {
    const rowBreakPoint = 5; //After every 5th row add a blank row
    let rows = '';
    let count = 0;
    const biList = basicIllustrationData.termInforceIllustration.payload.policies[0];
    noOfRows = startIndex + noOfRows < biList.policyYear.length ? startIndex + noOfRows : biList.policyYear.length;
    let columnHeaders: Array<string>;
    if (is99series) {
      columnHeaders = hasWaiver
        ? this.mmopm99TermInforceColumnHeaders
        : this.mmopm99TermInforceColumnHeadersWithoutWaiver;
    } else {
      columnHeaders = hasWaiver ? this.termInforceColumnHeaders : this.termInforceColumnHeadersWithoutWaiver;
    }

    for (let i = startIndex; i < noOfRows; i++) {
      let row = count % 2 === 0 ? `<TR class="odd-row">` : `<TR>`;
      columnHeaders.map((columnName) => {
        const cellValue = biList[columnName][i]
          ? Utils.formatNumber(Utils.roundAwayFromZero(biList[columnName][i], 0), 0)
          : 0;
        if (count === rowBreakPoint) {
          row += `<TD><p>&nbsp;</p></TD>`;
        } else {
          row += `<TD><p>${cellValue}</p></TD>`;
        }
      });
      if (count === 5) {
        count = 0;
        i--;
      } else {
        count++;
      }
      row += `</TR>`;
      rows += row;
    }
    return rows;
  }

  static checkIfRequestContainsRider(illustrationRequest, riderType: string): boolean {
    let containsRider: boolean = false;
    if (illustrationRequest && illustrationRequest.riders) {
      illustrationRequest.riders.forEach((rider) => {
        if (rider.type === riderType) {
          containsRider = true;
        }
      });
    }
    return containsRider;
  }

  static getTable(
    illustrationRequest,
    startIndex: number,
    noOfRows: number,
    basicIllustrationData,
    isWholeLifeInforce?: boolean,
    isWLFaceChange: boolean = false,
    tableColumns: string[] = [],
    columnHeaderText: string = ''
  ): string {
    const columnHeaders = columnHeaderText || this.getColumnHeaders(illustrationRequest);
    const tableHtml =
      '<TABLE id="illustration-table">' +
      columnHeaders +
      this.getRows(
        illustrationRequest,
        startIndex,
        noOfRows,
        basicIllustrationData,
        isWholeLifeInforce,
        isWLFaceChange,
        tableColumns
      ) +
      '</TABLE>';
    return tableHtml;
  }
  static getColumnHeaders(illustrationRequest): string {
    const dividendOption: string = illustrationRequest.dividendOption;
    if (this.checkIfRequestContainsRider(illustrationRequest, RiderType.RTR)) {
      return ColumnHeaderDefWithRtrMap[dividendOption];
    } else if (this.checkIfRequestContainsRider(illustrationRequest, RiderType.CIR)) {
      return ColumnHeaderDefWithCirMap[dividendOption];
    } else {
      return ColumnHeaderDefMap[dividendOption];
    }
  }
  static getTableTitle(isSupplemental?: boolean): string {
    return `<h1 class="p-center">${isSupplemental ? IllustrationHtml.supplementalTitle : IllustrationHtml.title}</h1>`;
  }
  static getFooter(dividendOption: string, illustrationRequest: IllustrationRequest, isSupplemental?: boolean): string {
    if (illustrationRequest.dividendInterestAdjustment > 0) {
      if (isSupplemental === true) {
        return IllustrationBasedOnDiv.SupplementalLowerDividendFooterMap[dividendOption]
          ? IllustrationBasedOnDiv.SupplementalLowerDividendFooterMap[dividendOption]
          : ' ';
      }

      // check if LowerDividendFooterMap exsist just in case someone didn't call createLowerDividendFooterMap first
      return IllustrationBasedOnDiv.LowerDividendFooterMap[dividendOption]
        ? IllustrationBasedOnDiv.LowerDividendFooterMap[dividendOption]
        : ' ';
    } else {
      const result = isSupplemental === true ? SupplementalFooterMap[dividendOption] : FooterMap[dividendOption];
      return result.replace(/{{RATE_YEAR}}/g, CoverpathUtils.getCurrentRateYear());
    }
  }
  static getRows(
    illustrationRequest,
    startIndex: number,
    noOfRows: number,
    basicIllustrationData,
    isWholeLifeInforce?: boolean,
    isWLFaceChange: boolean = false,
    tableColumns: string[] = []
  ) {
    const rowBreakPoint = 5; //After every 5th row add a blank row
    let rows = '';
    let count = 0;
    const biList =
      isWholeLifeInforce === true
        ? basicIllustrationData.wholeLifeInforceIllustration.payload.policy.illustrationList
        : basicIllustrationData.illustration.payload.policy.illustrationList;
    const dividendOption: string = illustrationRequest.dividendOption;
    noOfRows = startIndex + noOfRows < biList.length ? startIndex + noOfRows : biList.length;
    let hasHitAge100: boolean = false;
    let columnHeaders: Array<string> = [];
    if (isWLFaceChange) {
      columnHeaders = tableColumns;
    } else {
      if (this.checkIfRequestContainsRider(illustrationRequest, RiderType.RTR)) {
        columnHeaders = DividendWithRtrColumnHeaderMap[dividendOption];
      } else {
        columnHeaders = this.dividendColumnHeaderMap[dividendOption];
      }
    }
    for (let i = startIndex; i < noOfRows; i++) {
      let row = count % 2 === 0 ? `<TR class="odd-row">` : `<TR>`;
      if (columnHeaders) {
        columnHeaders.map((columnName, cellCount) => {
          const cellValue = biList[i][columnName]
            ? Utils.formatNumber(Utils.roundAwayFromZero(biList[i][columnName], 0), 0)
            : 0;
          if (count === rowBreakPoint && columnName !== 'guaranteedDeathBenefitEOY') {
            row += `<TD${cellCount === 4 && !isWLFaceChange ? ' class="td-sideline"' : ''}><p>&nbsp;</p></TD>`;
          } else if (count === rowBreakPoint && columnName === 'guaranteedDeathBenefitEOY') {
            row += `<TD class="td-sideline"><p>&nbsp;</p></TD>`;
          } else if (count !== rowBreakPoint && columnName !== 'guaranteedDeathBenefitEOY') {
            if (columnName === 'policyYear') {
              row += `<TD><p class="p-center"> ${cellValue} </p></TD>`;
            } else {
              row += `<TD${
                cellCount === 4 && !isWLFaceChange ? ' class="td-sideline"' : ''
              }><p>${this.getDisclaimerSymbolOrValue(cellValue, columnName, hasHitAge100)}</p></TD>`;
            }
          } else if (count !== rowBreakPoint && columnName === 'guaranteedDeathBenefitEOY') {
            row += `<TD class="td-sideline"><p> ${cellValue} </p></TD>`;
          }

          if (columnName === 'ageEndYear' && cellValue > 100) {
            hasHitAge100 = true;
          }
        });
      }
      if (count === 5) {
        count = 0;
        i--;
      } else {
        count++;
      }
      row += `</TR>`;
      rows += row;
    }
    return rows;
  }

  // since this file static the lower dividend option has dynamic content the LowerDividendFooterMap needs to bet set inside this function
  // and needs to be called before the LowerDividendFooterMap can be used (currently used in get footer)
  static createLowerDividendFooterMap(dividendAdjustmentPercent: number) {
    IllustrationBasedOnDiv.dividendAdjustmentPercent = CoverpathUtils.decimalToPercent(dividendAdjustmentPercent);

    IllustrationBasedOnDiv.LowerDividendFooterMap = {
      Cash: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Paid in Cash. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      PRPUA: `<div class="footer-text">
            <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Reduce Premium/Paid-Up Additions. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      PRCash: `<div class="footer-text">
            <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Reduce Premium/Cash. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      Accumulate: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Accumulate at Interest. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      PUA: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of <NOBR>Paid-Up</NOBR> Additions. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
    };

    IllustrationBasedOnDiv.SupplementalLowerDividendFooterMap = {
      TabularCash: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Paid in Cash. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      TabularPRPUA: `<div class="footer-text">
            <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Reduce Premium/Paid-Up Additions. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      TabularPRCash: `<div class="footer-text">
            <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Reduce Premium/Cash. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      TabularAccumulate: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Accumulate at Interest. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      TabularPUA: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of <NOBR>Paid-Up</NOBR> Additions. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
      SupplementalCash: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Paid in Cash. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
              new Date()
            )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
      SupplementalPRPUA: `<div class="footer-text">
            <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Reduce Premium/Paid-Up Additions. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
              new Date()
            )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
      SupplementalPRCash: `<div class="footer-text">
            <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Reduce Premium/Cash. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
              new Date()
            )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
      SupplementalAccumulate: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of Accumulate at Interest. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
              new Date()
            )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
      SupplementalPUA: `<div class="footer-text">
            <p class="p-left footer-text-bold">
            *This illustration reflects the dividend option of <NOBR>Paid-Up</NOBR> Additions. These illustrated amounts are not guaranteed. They reflect a hypothetical lower dividend which is ${
              IllustrationBasedOnDiv.dividendAdjustmentPercent
            }% below what is currently being used in the ${CoverpathUtils.getCurrentRateYear()}  dividend schedule and are neither estimates nor guarantees. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
            <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
              new Date()
            )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
    };
  }

  static getDisclaimerSymbolOrValue(cellValue: string | number, columnName: string, hasHitAge100: boolean): string {
    // For "Reduce Rate" dividend options, if the value would be 0 before the client has reached age 65/100,
    // we should show a symbol referring to the footnote disclaimer in the footer below the table

    if (columnName === 'netPremiumBegYear' && cellValue === 0 && !hasHitAge100) {
      return '+';
    }
    return `${cellValue}`;
  }

  static getSupplementalIllustrationTableBasedOnDiv(
    illustrationRequest,
    startIndex: number,
    noOfRows: number,
    illustrationData
  ) {
    return this.getSupplementalTable(illustrationRequest, startIndex, noOfRows, illustrationData);
  }

  static getSupplementalTable(illustrationRequest, startIndex: number, noOfRows: number, illustrationData): string {
    const columnHeaders = this.getSupplementalColumnHeaders();
    const rows = this.getConceptIllustrationRows(illustrationRequest, startIndex, noOfRows, illustrationData);
    const tableHtml = '<TABLE id="illustration-table">' + columnHeaders + rows + '</TABLE>';
    return tableHtml;
  }

  static getSupplementalColumnHeaders() {
    return ColumnHeaderDefMap['Concept'];
  }

  static getConceptIllustrationRows(illustrationRequest, startIndex: number, noOfRows: number, basicIllustrationData) {
    const rowBreakPoint = 5; //After every 5th row add a blank row
    let rows = '';
    let count = 0;
    const biList = basicIllustrationData.illustration.payload.policy.illustrationList;
    noOfRows = startIndex + noOfRows < biList.length ? startIndex + noOfRows : biList.length;
    let hasHitAge100: boolean = false;
    let columnHeaders: Array<string> = [];
    if (this.checkIfRequestContainsRider(illustrationRequest, RiderType.RTR)) {
      columnHeaders = DividendWithRtrColumnHeaderMap['Concept'];
    } else {
      columnHeaders = this.dividendColumnHeaderMap['Concept'];
    }
    for (let i = startIndex; i < noOfRows; i++) {
      let row = count % 2 === 0 ? `<TR class="odd-row">` : `<TR>`;
      if (columnHeaders) {
        columnHeaders.map((columnName) => {
          let cellValue = biList[i][columnName];
          if (columnName === 'annualLoan') {
            cellValue = biList[i]['annualLoanRequest'] + biList[i]['uncapitalizedLoanInterest'];
          }
          if (columnName === 'netCashValueEndYear') {
            cellValue = biList[i]['netCashValueEndYear'];
          }
          if (columnName === 'netDeathBenefitEndYear') {
            cellValue = biList[i]['netDeathBenefitEndYear'];
          }
          cellValue = cellValue ? Utils.formatNumber(Utils.roundAwayFromZero(cellValue, 0), 0) : 0;
          if (count === rowBreakPoint) {
            row += `<TD><p>&nbsp;</p></TD>`;
          } else if (count !== rowBreakPoint) {
            if (columnName === 'policyYear') {
              row += `<TD><p class="p-center"> ${cellValue} </p></TD>`;
            } else {
              row += `<TD><p>${this.getDisclaimerSymbolOrValue(cellValue, columnName, hasHitAge100)}</p></TD>`;
            }
          }

          if (columnName === 'ageEndYear' && cellValue > 100) {
            hasHitAge100 = true;
          }
        });
      }
      if (count === 5) {
        count = 0;
        i--;
      } else {
        count++;
      }
      row += `</TR>`;
      rows += row;
    }
    return rows;
  }
}

const FooterMap = {
  Cash: `<div class="footer-text">
        <p class="p-left footer-text-bold">
        *This illustration reflects the dividend option of Paid in Cash. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
        <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  PRPUA: `<div class="footer-text">
        <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
        <p class="p-left footer-text-bold">
        *This illustration reflects the dividend option of Reduce Premium/Paid-Up Additions. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
        <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  PRCash: `<div class="footer-text">
        <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
        <p class="p-left footer-text-bold">
        *This illustration reflects the dividend option of Reduce Premium/Cash. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
        <p class="p-left">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  Accumulate: `<div class="footer-text">
        <p class="p-left footer-text-bold">
        *This illustration reflects the dividend option of Accumulate at Interest. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
        <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  PUA: `<div class="footer-text">
        <p class="p-left footer-text-bold">
        *This illustration reflects the dividend option of <NOBR>Paid-Up</NOBR> Additions. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
        <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
};

const SupplementalFooterMap = {
  SupplementalCash: `<div class="footer-text">
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Paid in Cash. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
    new Date()
  )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
  TabularCash: `<div class="footer-text">
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Paid in Cash. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  SupplementalPRPUA: `<div class="footer-text">
  <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Reduce Premium/Paid-Up Additions. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
    new Date()
  )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
  TabularPRPUA: `<div class="footer-text">
  <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Reduce Premium/Paid-Up Additions. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  SupplementalPRCash: `<div class="footer-text">
  <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Reduce Premium/Cash. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
    new Date()
  )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
  TabularPRCash: `<div class="footer-text">
  <p class="p-left">+Dividends are used to reduce the current premium. A dividend schedule change may increase or decrease the net premium due each year.</p>
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Reduce Premium/Cash. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  SupplementalAccumulate: `<div class="footer-text">
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Accumulate at Interest. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
    new Date()
  )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
  TabularAccumulate: `<div class="footer-text">
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of Accumulate at Interest. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
  SupplementalPUA: `<div class="footer-text">
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of <NOBR>Paid-Up</NOBR> Additions. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information. This illustration is not valid unless accompanied by or preceded by a Basic Life Insurance Illustration dated ${Utils.formatDate(
    new Date()
  )}. Refer to the Basic Life Insurance Illustration for guaranteed elements and other important information.</p></div>`,
  TabularPUA: `<div class="footer-text">
  <p class="p-left footer-text-bold">
  *This illustration reflects the dividend option of <NOBR>Paid-Up</NOBR> Additions. <NOBR>Non-guaranteed</NOBR> values include dividends, which are neither estimates nor guarantees, but are based on the {{RATE_YEAR}} dividend schedule. The dividend schedule is reviewed annually and it is likely that dividends in future years will be lower or higher depending on the Company’s actual experience. For this reason, we strongly recommend that you look at a hypothetical lower schedule illustration available upon request.</p>
  <p class="p-left margin-top-10">Refer to the Narrative Summary for assumptions, explanations and additional information.</p></div>`,
};

const TermInforceColumnHeaders = `<TR>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <Th colspan=5 class="td-underline" align="center"><P class="p-center"><NOBR>Guaranteed</NOBR> Values</P></Th>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>Cost of</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>Total</P></TH>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>Age</P></TH>
                                    <TH><P>Annual</P></TH>
                                    <TH><P>Waiver of</P></TH>
                                    <TH><P>Total</P></TH>
                                    <TH><P>Cumulative</P></TH>
                                    <TH><P>Death</P></TH>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>End</P></TH>
                                    <TH><P>Premium for</P></TH>
                                    <TH><P>Premium</P></TH>
                                    <TH><P>Annual</P></TH>
                                    <TH><P>Total Annual</P></TH>
                                    <TH><P>Benefit</P></TH>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>Year</P></TH>
                                    <TH><P>Year</P></TH>
                                    <TH><P>Basic Policy</P></TH>
                                    <TH><P>Beg Year</P></TH>
                                    <TH><P>Premium</P></TH>
                                    <TH><P>Premium</P></TH>
                                    <TH><P>End Year</P></TH>
                                  </TR>`;
const TermInforceColumnHeadersWithoutWaiver = `<TR>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <Th colspan=4 class="td-underline" align="center"><P class="p-center"><NOBR>Guaranteed</NOBR> Values</P></Th>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>Total</P></TH>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>Age</P></TH>
                                                <TH><P>Annual</P></TH>
                                                <TH><P>Total</P></TH>
                                                <TH><P>Cumulative</P></TH>
                                                <TH><P>Death</P></TH>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>End</P></TH>
                                                <TH><P>Premium for</P></TH>
                                                <TH><P>Annual</P></TH>
                                                <TH><P>Total Annual</P></TH>
                                                <TH><P>Benefit</P></TH>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>Year</P></TH>
                                                <TH><P>Year</P></TH>
                                                <TH><P>Basic Policy</P></TH>
                                                <TH><P>Premium</P></TH>
                                                <TH><P>Premium</P></TH>
                                                <TH><P>End Year</P></TH>
                                              </TR>`;
const TermInforceColumnHeadersMMOPM99 = `<TR>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <Th colspan=5 class="td-underline" align="center"><P class="p-center"><NOBR>Guaranteed</NOBR> Values</P></Th>
                                    <Th colspan=2 class="td-underline" align="center"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values</P></Th>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>Cumulative</P></TH>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>Cost of</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>Total</P></TH>
                                    <TH><P>Current</P></TH>
                                    <TH><P>Current</P></TH>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>Age</P></TH>
                                    <TH><P>Annual</P></TH>
                                    <TH><P>Waiver of</P></TH>
                                    <TH><P>Total</P></TH>
                                    <TH><P>Cumulative</P></TH>
                                    <TH><P>Death</P></TH>
                                    <TH><P>Contract</P></TH>
                                    <TH><P>Contract</P></TH>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>&nbsp;</P></TH>
                                    <TH><P>End</P></TH>
                                    <TH><P>Premium for</P></TH>
                                    <TH><P>Premium</P></TH>
                                    <TH><P>Annual</P></TH>
                                    <TH><P>Total Annual</P></TH>
                                    <TH><P>Benefit</P></TH>
                                    <TH><P>Premium</P></TH>
                                    <TH><P>Premium</P></TH>
                                  </TR>
                                  <TR class="illustration-header-row">
                                    <TH><P>Year</P></TH>
                                    <TH><P>Year</P></TH>
                                    <TH><P>Basic Policy</P></TH>
                                    <TH><P>Beg Year</P></TH>
                                    <TH><P>Premium</P></TH>
                                    <TH><P>Premium</P></TH>
                                    <TH><P>End Year</P></TH>
                                    <TH><P>Beg Year</P></TH>
                                    <TH><P>Beg Year</P></TH>
                                  </TR>`;
const TermInforceColumnHeadersMMOPM99WithoutWaiver = `<TR>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <Th colspan=4 class="td-underline" align="center"><P class="p-center"><NOBR>Guaranteed</NOBR> Values</P></Th>
                                                <Th colspan=2 class="td-underline" align="center"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values</P></Th>
                                              </TR>
                                               <TR class="illustration-header-row">
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>Cumulative</P></TH>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>Total</P></TH>
                                                <TH><P>Current</P></TH>
                                                <TH><P>Current</P></TH>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>Age</P></TH>
                                                <TH><P>Annual</P></TH>
                                                <TH><P>Total</P></TH>
                                                <TH><P>Cumulative</P></TH>
                                                <TH><P>Death</P></TH>
                                                <TH><P>Contract</P></TH>
                                                <TH><P>Contract</P></TH>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>&nbsp;</P></TH>
                                                <TH><P>End</P></TH>
                                                <TH><P>Premium for</P></TH>
                                                <TH><P>Annual</P></TH>
                                                <TH><P>Total Annual</P></TH>
                                                <TH><P>Benefit</P></TH>
                                                <TH><P>Premium</P></TH>
                                                <TH><P>Premium</P></TH>
                                              </TR>
                                              <TR class="illustration-header-row">
                                                <TH><P>Year</P></TH>
                                                <TH><P>Year</P></TH>
                                                <TH><P>Basic Policy</P></TH>
                                                <TH><P>Premium</P></TH>
                                                <TH><P>Premium</P></TH>
                                                <TH><P>End Year</P></TH>
                                                <TH><P>Beg Year</P></TH>
                                                <TH><P>Beg Year</P></TH>
                                              </TR>`;

const ColumnHeaderDefMap = {
  Cash: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH ><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=6 class="td-underline" align="center"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Paid in</P></TH>
            <TH><P>Tax on</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Distribution</P></TH>
            <TH><P>Outlay</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><P>Year</P></TH>
            <TH><P>Year</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  PRPUA: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH ><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=7 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Value of</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><p>Year</P></TH>
            <TH><p>Year</P></TH>
            <TH><p>Premium</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
        </TR>`,
  PRCash: `<TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=8 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Paid in</P></TH>
            <TH><P>Tax on</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Distribution</P></TH>
            <TH><P>Outlay</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><p>Year</P></TH>
            <TH><p>Year</P></TH>
            <TH><p>Premium</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  Accumulate: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=7 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>

        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Tax on</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Value of</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Distribution</P></TH>
            <TH><P>Outlay</P></TH>
            <TH><P>Accums</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="illustration-header-row td-underline">
            <TH><P>Year</P></TH>
            <TH><P>Year</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  PUA: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH ><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=6 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P >Annual</P></TH>
            <TH><P>Value of</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><P>Year</P></TH>
            <TH><P>Year</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  Concept: `<TR>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <Th colspan=11 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
              </TR>
              <TR class="illustration-header-row">
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>Basic</P></TH>
                <TH><P>Total Cash</P></TH>
                <TH><P>Total</P></TH>
                <TH><P>Net</P></TH>
                <TH><P>Total</P></TH>
                <TH><P>Net</P></TH>
              </TR>
              <TR class="illustration-header-row">
                <TH><P>&nbsp;</P></TH>
                <TH><P>Age</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Policy</P></TH>
                <TH><P>Value of</P></TH>
                <TH><P>Outstanding</P></TH>
                <TH><P>Cash</P></TH>
                <TH><P><NOBR>Paid-Up</NOBR></P></TH>
                <TH><P>Death</P></TH>
              </TR>
              <TR class="illustration-header-row">
                <TH><P>&nbsp;</P></TH>
                <TH><P>End</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Net</P></TH>
                <TH><P>Dividend</P></TH>
                <TH><P>Cash Value</P></TH>
                <TH><P>Additions</P></TH>
                <TH><P>Loan</P></TH>
                <TH><P>Value</P></TH>
                <TH><P>Additions</P></TH>
                <TH><P>Benefit</P></TH>
              </TR>
              <TR class="td-underline illustration-header-row">
                <TH><P>Year</P></TH>
                <TH><P>Year</P></TH>
                <TH><P>Outlay</P></TH>
                <TH><P>Loan</P></TH>
                <TH><P>Surrender</P></TH>
                <TH><P>Outlay</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
              </TR>`,
};

const ColumnHeaderDefWithCirMap = {
  Cash: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH ><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=6 class="td-underline" align="center"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Paid in</P></TH>
            <TH><P>Tax on</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Distribution</P></TH>
            <TH><P>Outlay</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><P>Year</P></TH>
            <TH><P>Year</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  PRPUA: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH ><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=7 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Value of</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><p>Year</P></TH>
            <TH><p>Year</P></TH>
            <TH><p>Premium</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
        </TR>`,
  PRCash: `<TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=8 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Paid in</P></TH>
            <TH><P>Tax on</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Distribution</P></TH>
            <TH><P>Outlay</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><p>Year</P></TH>
            <TH><p>Year</P></TH>
            <TH><p>Premium</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>Beg Year</P></TH>
            <TH><p>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  Accumulate: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=7 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>

        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Annual</P></TH>
            <TH><P>Tax on</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Value of</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Distribution</P></TH>
            <TH><P>Outlay</P></TH>
            <TH><P>Accums</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="illustration-header-row td-underline">
            <TH><P>Year</P></TH>
            <TH><P>Year</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>Beg Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  PUA: `<TR>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH ><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <Th colspan=6 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>Guaranteed</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Total</P></TH>
            <TH><P>Total</P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>Age</P></TH>
            <TH><P>&nbsp;</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P>Death</P></TH>
            <TH><P>Net</P></TH>
            <TH><P>Value of</P></TH>
            <TH><P>Cash</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
            <TH><P>Death</P></TH>
            <TH><P><NOBR>Paid-Up</NOBR></P></TH>
        </TR>
        <TR class="illustration-header-row">
            <TH><P>&nbsp;</P></TH>
            <TH><P>End</P></TH>
            <TH><P>Contract</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Dividend</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Value</P></TH>
            <TH><P>Additions</P></TH>
            <TH><P>Benefit</P></TH>
            <TH><P>Insurance</P></TH>
        </TR>
        <TR class="td-underline illustration-header-row">
            <TH><P>Year</P></TH>
            <TH><P>Year</P></TH>
            <TH><P>Premium</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
            <TH><P>End Year</P></TH>
        </TR>`,
  Concept: `<TR>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <Th colspan=11 class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></Th>
              </TR>
              <TR class="illustration-header-row">
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>Basic</P></TH>
                <TH><P>Total Cash</P></TH>
                <TH><P>Total</P></TH>
                <TH><P>Net</P></TH>
                <TH><P>Total</P></TH>
                <TH><P>Net</P></TH>
              </TR>
              <TR class="illustration-header-row">
                <TH><P>&nbsp;</P></TH>
                <TH><P>Age</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>&nbsp;</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Policy</P></TH>
                <TH><P>Value of</P></TH>
                <TH><P>Outstanding</P></TH>
                <TH><P>Cash</P></TH>
                <TH><P><NOBR>Paid-Up</NOBR></P></TH>
                <TH><P>Death</P></TH>
              </TR>
              <TR class="illustration-header-row">
                <TH><P>&nbsp;</P></TH>
                <TH><P>End</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Annual</P></TH>
                <TH><P>Net</P></TH>
                <TH><P>Dividend</P></TH>
                <TH><P>Cash Value</P></TH>
                <TH><P>Additions</P></TH>
                <TH><P>Loan</P></TH>
                <TH><P>Value</P></TH>
                <TH><P>Additions</P></TH>
                <TH><P>Benefit</P></TH>
              </TR>
              <TR class="td-underline illustration-header-row">
                <TH><P>Year</P></TH>
                <TH><P>Year</P></TH>
                <TH><P>Outlay</P></TH>
                <TH><P>Loan</P></TH>
                <TH><P>Surrender</P></TH>
                <TH><P>Outlay</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
                <TH><P>End Year</P></TH>
              </TR>`,
};
